import React, { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import useMediaQuery from '../../hooks/useMediaQuery';

export default function TopNav() {
  const { creator, logout } = useAuth();
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 767px)');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuTriggerRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setMenuOpen(false);
        menuTriggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [menuOpen]);

  const navItems = [
    { to: '/dashboard', label: 'Dashboard' },
    { to: '/campaigns', label: 'Campaigns' },
    { to: '/payments', label: 'Payments' },
    { to: '/settings', label: 'Settings' },
  ];
  const initial = (creator?.name || creator?.displayName || creator?.twitterUsername || 'C').trim().charAt(0).toUpperCase();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavClick = () => {
    setMenuOpen(false);
  };

  return (
    <>
      <div className="topnav">
        <div className="topnav-left">
          <button
            type="button"
            className="topnav-logo"
            onClick={() => navigate('/dashboard')}
            aria-label="TAKEOVER creator dashboard"
          >
            TAKEOVER
          </button>
          {!isMobile && <div className="topnav-tag">Creator</div>}
          {!isMobile && (
            <nav className="topnav-nav">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `topnav-item${isActive ? ' active' : ''}`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
          )}
        </div>
        <div className="topnav-right">
          {!isMobile && (
            <div style={{ textAlign: 'right' }}>
              <div className="topnav-name">
                {creator?.name || creator?.displayName || 'Creator'}
              </div>
              <div className="topnav-handle">
                {(creator?.twitterUsername || creator?.twitterHandle) ? `@${creator.twitterUsername || creator.twitterHandle}` : ''}
              </div>
            </div>
          )}
          <div className="topnav-pfp">
            {creator?.profileImageUrl ? (
              <img
                src={creator.profileImageUrl}
                alt="Profile"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <span>{initial}</span>
            )}
          </div>
          {isMobile ? (
            <button
              className="topnav-hamburger"
              ref={menuTriggerRef}
              onClick={() => setMenuOpen((v) => !v)}
              aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={menuOpen}
              aria-controls="creator-mobile-navigation"
            >
              {menuOpen ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              )}
            </button>
          ) : (
            <button className="topnav-logout" onClick={handleLogout} title="Log out">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Mobile dropdown menu */}
      {isMobile && menuOpen && (
        <div className="topnav-mobile-menu" id="creator-mobile-navigation">
          <div className="topnav-mobile-user">
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)' }}>
              {creator?.name || creator?.displayName || 'Creator'}
            </div>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: '#555', marginTop: 2 }}>
              {(creator?.twitterUsername || creator?.twitterHandle) ? `@${creator.twitterUsername || creator.twitterHandle}` : ''}
            </div>
          </div>
          <nav aria-label="Creator portal">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `topnav-mobile-item${isActive ? ' active' : ''}`
                }
                onClick={handleNavClick}
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <button className="topnav-mobile-logout" onClick={handleLogout}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
            Log Out
          </button>
        </div>
      )}

      {isMobile && (
        <nav className="bottomnav" aria-label="Primary">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `bottomnav-item${isActive ? ' active' : ''}`
              }
              onClick={handleNavClick}
            >
              <span className="bottomnav-dot" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
      )}
    </>
  );
}
